import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "@/contexts/OrganizationContext";

export interface GdprConsentRecord {
  id: string;
  organization_id: string;
  person_id: string | null;
  profile_user_id: string | null;
  consent_type: string;
  consent_version: string;
  consent_text: string;
  event: "granted" | "withdrawn" | "updated";
  granted_at: string | null;
  withdrawn_at: string | null;
  created_at: string;
  collection_method: string;
  collected_by: string | null;
}

export interface InsertConsentRecord {
  organization_id: string;
  person_id?: string | null;
  profile_user_id?: string | null;
  consent_type: string;
  consent_version?: string;
  consent_text: string;
  event: "granted" | "withdrawn" | "updated";
  granted_at?: string;
  withdrawn_at?: string | null;
  collection_method: string;
  collected_by?: string | null;
}

const QUERY_KEY = "gdpr-consent-records";

export function useGdprConsentRecords(filters?: {
  consentType?: string;
  event?: string;
}) {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: [QUERY_KEY, selectedOrganization?.id, filters],
    queryFn: async (): Promise<GdprConsentRecord[]> => {
      if (!selectedOrganization?.id) return [];

      let query = supabase
        .from("gdpr_consent_records")
        .select("*")
        .eq("organization_id", selectedOrganization.id)
        .order("created_at", { ascending: false });

      if (filters?.consentType) {
        query = query.eq("consent_type", filters.consentType);
      }
      if (filters?.event) {
        query = query.eq("event", filters.event);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as GdprConsentRecord[];
    },
    enabled: !!selectedOrganization?.id,
  });
}

export function useInsertConsentRecord() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (record: InsertConsentRecord) => {
      const { data, error } = await supabase
        .from("gdpr_consent_records")
        .insert([record])
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
}

/** Check if active consent exists for a subject + type */
export function useHasActiveConsent(
  subjectType: "person" | "profile",
  subjectId: string | undefined,
  consentType: string
) {
  const { selectedOrganization } = useOrganization();

  return useQuery({
    queryKey: [QUERY_KEY, "active", subjectType, subjectId, consentType],
    queryFn: async (): Promise<boolean> => {
      if (!selectedOrganization?.id || !subjectId) return false;

      const subjectColumn = subjectType === "person" ? "person_id" : "profile_user_id";

      // Check for the latest event for this subject + type
      const { data, error } = await supabase
        .from("gdpr_consent_records")
        .select("event")
        .eq("organization_id", selectedOrganization.id)
        .eq(subjectColumn, subjectId)
        .eq("consent_type", consentType)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data?.event === "granted";
    },
    enabled: !!selectedOrganization?.id && !!subjectId,
  });
}
